import { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "motion/react";
import { useNavigate } from "react-router-dom";
import PawLogo from "../ui/PawLogo";

function useIsDesktop(breakpointPx = 768) {
    const [isDesktop, setIsDesktop] = useState(() =>
        typeof window !== "undefined"
            ? window.matchMedia(`(min-width:${breakpointPx}px)`).matches
            : true
    );

    useEffect(() => {
        const mq = window.matchMedia(`(min-width:${breakpointPx}px)`);
        const handler = (e) => setIsDesktop(e.matches);

        mq.addEventListener?.("change", handler);
        mq.addListener?.(handler);

        setIsDesktop(mq.matches);

        return () => {
            mq.removeEventListener?.("change", handler);
            mq.removeListener?.(handler);
        };
    }, [breakpointPx]);

    return isDesktop;
}

function stripMediaKeys(styleObj) {
    if (!styleObj || typeof styleObj !== "object") return styleObj;

    const cleaned = {};
    for (const [key, value] of Object.entries(styleObj)) {
        if (typeof key === "string" && key.trim().startsWith("@media")) continue;
        cleaned[key] = value;
    }
    return cleaned;
}

export default function Header({
                                   kennelName,
                                   primaryColor,
                                   secondaryColor,
                                   activeNavItem,
                                   isMenuOpen,
                                   onToggleMenu,
                                   navItems,
                                   onNavClick,
                                   styles,
                                   contact, // { phone, email }
                               }) {
    const navigate = useNavigate();
    const isDesktop = useIsDesktop(768);

    const [isLittersOpenMobile, setIsLittersOpenMobile] = useState(false);
    const [isLittersOpenDesktop, setIsLittersOpenDesktop] = useState(false);

    const littersKey = "litters";
    const littersLabel = "Würfe";


    // Filtere "puppies" (alte A-Wurf-Section) und "litter-a" (Route) aus der Hauptnav,
    // weil A-Wurf im Dropdown unter "Würfe" erscheint.
    const navWithoutPuppies = useMemo(
        () => (navItems || []).filter((i) => i.key !== "puppies" && i.key !== "litter-a"),
        [navItems]
    );

    // ✅ Index, nach dem "Würfe" eingefügt wird (nach "Hündinnen & Rüden")
    const insertLittersAfterIndex = useMemo(() => {
        const items = navWithoutPuppies || [];

        // 1) Am besten per key matchen (trag hier den echten key ein, falls du ihn kennst)
        const possibleDogsKeys = ["dogs", "breeding", "hunde", "females", "males"];
        let idx = items.findIndex((i) => possibleDogsKeys.includes(i.key));

        // 2) Fallback: per Label (falls Keys unbekannt / variabel sind)
        if (idx === -1) {
            idx = items.findIndex((i) => /Hündinnen\s*&\s*Rüden/i.test(i.label));
        }

        // 3) Wenn gar nicht gefunden: ans Ende
        return idx !== -1 ? idx : items.length - 1;
    }, [navWithoutPuppies]);

    const phone = contact?.phone || "+49 175 3203089";
    const email = contact?.email || "Havaneser@vom-schnuesterhus.de";
    const telHref = `tel:${phone.replace(/\s+/g, "")}`;
    const mailHref = `mailto:${email}`;

    // ✅ WhatsApp
    const whatsappNumber = "+49 175 3203089";
    const waHref = `https://wa.me/${whatsappNumber.replace(/[^\d]/g, "")}`;

    const littersWrapRef = useRef(null);

    // ✅ Styles "säubern", damit keine @media Keys an React style={} gehen
    const s = useMemo(() => {
        const base = styles || {};
        return {
            header: stripMediaKeys(base.header),
            logoContainer: stripMediaKeys(base.logoContainer),
            logoText: stripMediaKeys(base.logoText),
            mobileMenuButton: stripMediaKeys(base.mobileMenuButton),
            menuBar: stripMediaKeys(base.menuBar),
            navItem: stripMediaKeys(base.navItem),
        };
    }, [styles]);

    // Klick außerhalb schließt Desktop Dropdown
    useEffect(() => {
        if (!isDesktop) return;

        const onDown = (e) => {
            if (!isLittersOpenDesktop) return;
            if (!littersWrapRef.current) return;
            if (!littersWrapRef.current.contains(e.target)) {
                setIsLittersOpenDesktop(false);
            }
        };

        document.addEventListener("mousedown", onDown);
        return () => document.removeEventListener("mousedown", onDown);
    }, [isDesktop, isLittersOpenDesktop]);

    // Mobile UX: ESC schließt, Body-Scroll lock solange Drawer offen
    useEffect(() => {
        if (isDesktop) return;

        const onKeyDown = (e) => {
            if (e.key === "Escape" && isMenuOpen) onToggleMenu?.();
        };

        document.addEventListener("keydown", onKeyDown);

        const prevOverflow = document.body.style.overflow;
        if (isMenuOpen) document.body.style.overflow = "hidden";
        else document.body.style.overflow = prevOverflow || "";

        return () => {
            document.removeEventListener("keydown", onKeyDown);
            document.body.style.overflow = prevOverflow || "";
        };
    }, [isMenuOpen, isDesktop, onToggleMenu]);

    // Zentrale Navigation: scrollt bei ref-Objekt, routet bei String
    const handleNavigate = (refOrRoute, key) => {
        // Route
        if (typeof refOrRoute === "string") {
            navigate(refOrRoute);
            setIsLittersOpenMobile(false);
            setIsLittersOpenDesktop(false);
            if (isMenuOpen) onToggleMenu?.();
            return;
        }

        // Scroll (Refs)
        onNavClick?.(refOrRoute, key);
        setIsLittersOpenMobile(false);
        setIsLittersOpenDesktop(false);
        if (isMenuOpen) onToggleMenu?.();
    };

    // A-Wurf: bevorzugt Route-Item "litter-a", sonst Fallback auf alte Section "puppies"
    const goToAWurf = () => {
        const aWurfItem =
            (navItems || []).find((i) => i.key === "litter-a") ||
            (navItems || []).find((i) => i.key === "puppies");

        if (aWurfItem) handleNavigate(aWurfItem.ref, aWurfItem.key);
    };

    const overlayStyle = {
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.35)",
        zIndex: 3000,
    };

    const drawerStyle = {
        position: "fixed",
        top: 0,
        right: 0,
        height: "100dvh",
        width: "86vw",
        maxWidth: 380,
        background: "#fff",
        zIndex: 3001,
        boxShadow: "0 18px 40px rgba(0,0,0,0.18)",
        display: "flex",
        flexDirection: "column",
        padding: "18px 16px 16px",
    };

    const closeBtnStyle = {
        width: 42,
        height: 42,
        borderRadius: 12,
        border: "1px solid rgba(0,0,0,0.08)",
        background: "#fafafa",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
    };

    const DesktopLittersDropdown = (
        <li
            ref={littersWrapRef}
            style={{
                ...s.navItem,
                margin: "0 15px",
                position: "relative",
                color: activeNavItem === littersKey ? primaryColor : "inherit",
                userSelect: "none",
            }}
        >
            <motion.button
                type="button"
                onClick={() => setIsLittersOpenDesktop((v) => !v)}
                whileHover={{ scale: 1.05, color: primaryColor }}
                style={{
                    all: "unset",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    justifyContent: "center",
                }}
                aria-haspopup="menu"
                aria-expanded={isLittersOpenDesktop}
            >
                {littersLabel}
                <span style={{ fontSize: 12, opacity: 0.7 }}>▾</span>
            </motion.button>

            <AnimatePresence>
                {isLittersOpenDesktop && (
                    <motion.div
                        initial={{ opacity: 0, y: -6 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -6 }}
                        transition={{ duration: 0.15 }}
                        style={{
                            position: "absolute",
                            top: "calc(100% + 10px)",
                            left: "50%",
                            transform: "translateX(-50%)",
                            background: "#fff",
                            borderRadius: 12,
                            boxShadow: "0 10px 25px rgba(0,0,0,0.12)",
                            padding: "10px 0",
                            minWidth: 180,
                            zIndex: 2000,
                            border: "1px solid rgba(0,0,0,0.06)",
                        }}
                        role="menu"
                    >
                        <motion.button
                            type="button"
                            onClick={goToAWurf}
                            whileHover={{ backgroundColor: "rgba(0,0,0,0.03)" }}
                            style={{
                                width: "100%",
                                padding: "10px 14px",
                                cursor: "pointer",
                                whiteSpace: "nowrap",
                                textAlign: "center",
                                border: "none",
                                background: "transparent",
                                font: "inherit",
                            }}
                            role="menuitem"
                        >
                            A-Wurf
                        </motion.button>
                    </motion.div>
                )}
            </AnimatePresence>
        </li>
    );

    const MobileLittersDropdown = (
        <>
            <motion.li
                whileTap={{ scale: 0.98 }}
                onClick={() => setIsLittersOpenMobile((v) => !v)}
                style={{
                    padding: "12px 12px",
                    borderRadius: 14,
                    cursor: "pointer",
                    fontWeight: 700,
                    background: activeNavItem === littersKey ? "rgba(0,0,0,0.04)" : "transparent",
                    color: activeNavItem === littersKey ? primaryColor : "#222",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                }}
            >
                <span>{littersLabel}</span>
                <span style={{ fontSize: 12, opacity: 0.7 }}>{isLittersOpenMobile ? "▴" : "▾"}</span>
            </motion.li>

            <AnimatePresence>
                {isLittersOpenMobile && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        style={{ overflow: "hidden" }}
                    >
                        <div style={{ padding: "6px 6px 2px 6px" }}>
                            <motion.div
                                onClick={goToAWurf}
                                whileHover={{ scale: 1.01 }}
                                whileTap={{ scale: 0.98 }}
                                style={{
                                    padding: "12px 12px",
                                    borderRadius: 12,
                                    cursor: "pointer",
                                    background: "rgba(0,0,0,0.03)",
                                    color: "#333",
                                    textAlign: "left",
                                    fontWeight: 650,
                                    border: "1px solid rgba(0,0,0,0.06)",
                                }}
                            >
                                A-Wurf
                            </motion.div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );

    return (
        <header style={s.header}>
            <div
                style={{
                    width: "100%",
                    display: "grid",
                    gridTemplateColumns: "1fr auto 1fr",
                    alignItems: "center",
                    gap: 10,
                }}
            >
                {/* Logo links */}
                <motion.div
                    style={{ ...s.logoContainer, justifySelf: "start" }}
                    whileHover={{ scale: 1.05 }}
                    onClick={() => handleNavigate("/", "home")}
                >
                    <PawLogo color={primaryColor} size={40} />
                    <div style={s.logoText}>
                        <h1
                            style={{
                                color: primaryColor,
                                margin: 0,
                                fontFamily: "'Playfair Display', serif",
                            }}
                        >
                            {kennelName}
                        </h1>
                        <p style={{ color: secondaryColor, margin: 0, fontSize: "14px" }}>
                            Liebevolle Havaneser Zucht
                        </p>
                    </div>
                </motion.div>

                {/* Mitte */}
                <div style={{ justifySelf: "center" }}>
                    {!isDesktop ? (
                        <motion.div
                            style={s.mobileMenuButton}
                            onClick={onToggleMenu}
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.95 }}
                            aria-label={isMenuOpen ? "Menü schließen" : "Menü öffnen"}
                            aria-expanded={isMenuOpen}
                        >
                            <div style={{ ...s.menuBar, backgroundColor: primaryColor }} />
                            <div style={{ ...s.menuBar, backgroundColor: primaryColor }} />
                            <div style={{ ...s.menuBar, backgroundColor: primaryColor }} />
                        </motion.div>
                    ) : (
                        <nav style={{ display: "flex", alignItems: "center" }}>
                            <ul
                                style={{
                                    display: "flex",
                                    listStyle: "none",
                                    margin: 0,
                                    padding: 0,
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                {navWithoutPuppies.map((item, idx) => (
                                    <span key={`wrap-${item.key}`} style={{ display: "contents" }}>
                    <motion.li
                        key={item.key}
                        style={{
                            ...s.navItem,
                            margin: "0 15px",
                            color: activeNavItem === item.key ? primaryColor : "inherit",
                        }}
                        onClick={() => handleNavigate(item.ref, item.key)}
                        whileHover={{ scale: 1.05, color: primaryColor }}
                    >
                      {item.label}
                    </motion.li>

                                        {/* ✅ Würfe nach Hündinnen/Rüden einfügen */}
                                        {idx === insertLittersAfterIndex && DesktopLittersDropdown}
                  </span>
                                ))}

                                {/* Falls es gar keine Items gibt: Dropdown trotzdem anzeigen */}
                                {navWithoutPuppies.length === 0 && DesktopLittersDropdown}
                            </ul>
                        </nav>
                    )}
                </div>

                {/* Rechts: Icons (nur Desktop) */}
                <div style={{ justifySelf: "end", display: "flex", alignItems: "center", gap: 12 }}>
                    {isDesktop && (
                        <>
                            <motion.a
                                href={telHref}
                                title={phone}
                                aria-label="Anrufen"
                                whileHover={{ scale: 1.08 }}
                                whileTap={{ scale: 0.95 }}
                                style={{
                                    width: 40,
                                    height: 40,
                                    borderRadius: "50%",
                                    backgroundColor: "#f5f5f5",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    color: primaryColor,
                                    textDecoration: "none",
                                }}
                            >
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                    <path
                                        d="M22 16.92V21a2 2 0 0 1-2.18 2 19.86 19.86 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.86 19.86 0 0 1 2 4.18 2 2 0 0 1 4 2h4.09a2 2 0 0 1 2 1.72c.12.81.3 1.6.54 2.36a2 2 0 0 1-.45 2.11L8.09 10.09a16 16 0 0 0 6 6l1.9-2.09a2 2 0 0 1 2.11-.45c.76.24 1.55.42 2.36.54A2 2 0 0 1 22 16.92Z"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                            </motion.a>

                            <motion.a
                                href={mailHref}
                                title={email}
                                aria-label="E-Mail senden"
                                whileHover={{ scale: 1.08 }}
                                whileTap={{ scale: 0.95 }}
                                style={{
                                    width: 40,
                                    height: 40,
                                    borderRadius: "50%",
                                    backgroundColor: "#f5f5f5",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    color: primaryColor,
                                    textDecoration: "none",
                                }}
                            >
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                    <path
                                        d="M4 4H20C21.1 4 22 4.9 22 6V18C22 19.1 21.1 20 20 20H4C2.9 20 2 19.1 2 18V6C2 4.9 2.9 4 4 4Z"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                    <path
                                        d="M22 6L12 13L2 6"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                            </motion.a>

                            {/* ✅ WhatsApp */}
                            <motion.a
                                href={waHref}
                                title={`WhatsApp: ${whatsappNumber}`}
                                aria-label="WhatsApp Chat öffnen"
                                target="_blank"
                                rel="noopener noreferrer"
                                whileHover={{ scale: 1.08 }}
                                whileTap={{ scale: 0.95 }}
                                style={{
                                    width: 40,
                                    height: 40,
                                    borderRadius: "50%",
                                    backgroundColor: "#f5f5f5",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    color: primaryColor,
                                    textDecoration: "none",
                                }}
                            >
                                <svg width="20" height="20" viewBox="0 0 32 32" fill="none" aria-hidden="true">
                                    <path
                                        d="M16 3C9.372 3 4 8.115 4 14.425c0 2.454.82 4.73 2.213 6.59L5 29l8.17-1.98a12.6 12.6 0 0 0 2.83.32c6.628 0 12-5.115 12-11.425C28 8.115 22.628 3 16 3Z"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinejoin="round"
                                    />
                                    <path
                                        d="M12.6 10.6c-.33-.75-.68-.77-.99-.78-.26-.01-.56-.01-.86-.01-.3 0-.79.11-1.2.55-.41.44-1.57 1.49-1.57 3.63 0 2.14 1.61 4.2 1.84 4.49.23.29 3.11 4.83 7.67 6.58 3.79 1.46 4.57 1.17 5.39 1.09.82-.08 2.63-1.05 3-2.07.37-1.02.37-1.89.26-2.07-.11-.18-.41-.29-.86-.5-.45-.21-2.63-1.24-3.03-1.38-.4-.14-.69-.21-.99.29-.3.5-1.14 1.38-1.4 1.66-.26.29-.52.32-.97.11-.45-.21-1.9-.68-3.62-2.17-1.34-1.14-2.24-2.55-2.5-2.98-.26-.43-.03-.66.2-.87.2-.19.45-.5.68-.75.23-.25.3-.43.45-.72.15-.29.08-.54-.04-.75-.12-.21-1.04-2.55-1.43-3.42Z"
                                        fill="currentColor"
                                    />
                                </svg>
                            </motion.a>
                        </>
                    )}
                </div>
            </div>

            {/* MOBILE: Off-Canvas Drawer */}
            {!isDesktop && (
                <AnimatePresence>
                    {isMenuOpen && (
                        <>
                            <motion.div
                                key="overlay"
                                style={overlayStyle}
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                onClick={onToggleMenu}
                                aria-hidden="true"
                            />

                            <motion.aside
                                key="drawer"
                                style={drawerStyle}
                                initial={{ x: "100%" }}
                                animate={{ x: 0 }}
                                exit={{ x: "100%" }}
                                transition={{ type: "spring", stiffness: 380, damping: 36 }}
                                role="dialog"
                                aria-modal="true"
                                aria-label="Mobiles Menü"
                            >
                                <div
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        marginBottom: 14,
                                        gap: 12,
                                    }}
                                >
                                    <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.1 }}>
                                        <div style={{ fontWeight: 700, color: primaryColor }}>Menü</div>
                                        <div style={{ fontSize: 12, color: secondaryColor }}>{kennelName}</div>
                                    </div>

                                    <motion.button
                                        type="button"
                                        onClick={onToggleMenu}
                                        style={closeBtnStyle}
                                        whileHover={{ scale: 1.05 }}
                                        whileTap={{ scale: 0.95 }}
                                        aria-label="Menü schließen"
                                    >
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                                            <path
                                                d="M18 6L6 18M6 6l12 12"
                                                stroke="currentColor"
                                                strokeWidth="2.2"
                                                strokeLinecap="round"
                                            />
                                        </svg>
                                    </motion.button>
                                </div>

                                <div style={{ overflowY: "auto", paddingRight: 2 }}>
                                    <ul
                                        style={{
                                            listStyle: "none",
                                            margin: 0,
                                            padding: 0,
                                            display: "flex",
                                            flexDirection: "column",
                                            gap: 6,
                                        }}
                                    >
                                        {navWithoutPuppies.map((item, idx) => (
                                            <span key={`mwrap-${item.key}`} style={{ display: "contents" }}>
                        <motion.li
                            key={item.key}
                            onClick={() => handleNavigate(item.ref, item.key)}
                            whileTap={{ scale: 0.98 }}
                            style={{
                                padding: "12px 12px",
                                borderRadius: 14,
                                cursor: "pointer",
                                fontWeight: 600,
                                background:
                                    activeNavItem === item.key ? "rgba(0,0,0,0.04)" : "transparent",
                                color: activeNavItem === item.key ? primaryColor : "#222",
                            }}
                        >
                          {item.label}
                        </motion.li>

                                                {/* ✅ Würfe an der richtigen Stelle (Mobile) */}
                                                {idx === insertLittersAfterIndex && MobileLittersDropdown}
                      </span>
                                        ))}

                                        {/* Falls keine Items: Dropdown trotzdem zeigen */}
                                        {navWithoutPuppies.length === 0 && MobileLittersDropdown}
                                    </ul>

                                    {/* Mobile: Kontakt-Buttons */}
                                    <div style={{ marginTop: 16, display: "flex", gap: 10 }}>
                                        <motion.a
                                            href={telHref}
                                            whileTap={{ scale: 0.98 }}
                                            style={{
                                                flex: 1,
                                                padding: "12px 12px",
                                                borderRadius: 14,
                                                border: "1px solid rgba(0,0,0,0.08)",
                                                textDecoration: "none",
                                                color: primaryColor,
                                                fontWeight: 700,
                                                textAlign: "center",
                                            }}
                                        >
                                            Anrufen
                                        </motion.a>

                                        <motion.a
                                            href={mailHref}
                                            whileTap={{ scale: 0.98 }}
                                            style={{
                                                flex: 1,
                                                padding: "12px 12px",
                                                borderRadius: 14,
                                                border: "1px solid rgba(0,0,0,0.08)",
                                                textDecoration: "none",
                                                color: primaryColor,
                                                fontWeight: 700,
                                                textAlign: "center",
                                            }}
                                        >
                                            E-Mail
                                        </motion.a>

                                        {/* ✅ WhatsApp Button (Mobile) */}
                                        <motion.a
                                            href={waHref}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            whileTap={{ scale: 0.98 }}
                                            style={{
                                                flex: 1,
                                                padding: "12px 12px",
                                                borderRadius: 14,
                                                border: "1px solid rgba(0,0,0,0.08)",
                                                textDecoration: "none",
                                                color: primaryColor,
                                                fontWeight: 700,
                                                textAlign: "center",
                                            }}
                                        >
                                            WhatsApp
                                        </motion.a>
                                    </div>
                                </div>
                            </motion.aside>
                        </>
                    )}
                </AnimatePresence>
            )}
        </header>
    );
}
